import React from 'react';
import { AddLineInput, CheckoutPaymentInput, UUID } from '../types';
import { cartService } from '../services/cartService';

interface CartState {
	 cartId: UUID | null;
	 busy: boolean;
	 error: string | null;
}

interface CartContextValue extends CartState {
	 ensureCart: (businessId: UUID, locationId: UUID, customerId?: UUID | null) => Promise<void>;
	 addLine: (input: AddLineInput) => Promise<void>;
	 checkout: (payments: CheckoutPaymentInput[]) => Promise<UUID>;
}

const CartContext = React.createContext<CartContextValue | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	 const [cartId, setCartId] = React.useState<UUID | null>(null);
	 const [busy, setBusy] = React.useState(false);
	 const [error, setError] = React.useState<string | null>(null);
	 const businessRef = React.useRef<UUID | null>(null);
	 const locationRef = React.useRef<UUID | null>(null);

	 const ensureCart = React.useCallback(async (businessId: UUID, locationId: UUID, customerId?: UUID | null) => {
		 setBusy(true); setError(null);
		 try {
			 const { cartId } = await cartService.ensureCart(businessId, locationId, customerId);
			 setCartId(cartId);
			 businessRef.current = businessId;
			 locationRef.current = locationId;
		 } catch (e) {
			 setError(e instanceof Error ? e.message : 'Failed to ensure cart');
		 } finally {
			 setBusy(false);
		 }
	 }, []);

	 const addLine = React.useCallback(async (input: AddLineInput) => {
		 let targetCartId = cartId;
		 if (!targetCartId && businessRef.current && locationRef.current) {
			 try {
				 const { cartId: newId } = await cartService.ensureCart(businessRef.current, locationRef.current, null);
				 setCartId(newId);
				 targetCartId = newId;
			 } catch {}
		 }
		 if (!targetCartId) throw new Error('Cart not initialized');
		 setBusy(true); setError(null);
		 try {
			 await cartService.addLine(targetCartId, input);
		 } catch (e) {
			 setError(e instanceof Error ? e.message : 'Failed to add line');
		 } finally {
			 setBusy(false);
		 }
	 }, [cartId]);

	 const checkout = React.useCallback(async (payments: CheckoutPaymentInput[]) => {
		 if (!cartId) throw new Error('Cart not initialized');
		 setBusy(true); setError(null);
		 try {
			 const { orderId } = await cartService.checkout(cartId, payments);
			 return orderId;
		 } catch (e) {
			 setError(e instanceof Error ? e.message : 'Failed to checkout');
			 throw e;
		 } finally {
			 setBusy(false);
		 }
	 }, [cartId]);

	 const value: CartContextValue = React.useMemo(() => ({
		 cartId, busy, error,
		 ensureCart, addLine, checkout
	 }), [cartId, busy, error, ensureCart, addLine, checkout]);

	 return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export function useCartContext(): CartContextValue {
	 const ctx = React.useContext(CartContext);
	 if (!ctx) throw new Error('useCartContext must be used within CartProvider');
	 return ctx;
}


